<template>
  <div class="vz-individual-order-cargo-dimensions">
    <vz-measure-input
      :modelValue="detailLength"
      :disabled="getDisabled('length')"
      label="Длина"
      class="small-width"
      placeholder="0.2"
      big
      measure="м"
      :max-value="maxDetailLength"
      :min-value="0.01"
      :clearable="clearable"
      with-max-notice
      @change="changeDetailParam('length', $event)"
    />
    <vz-measure-input
      :modelValue="detailWidth"
      :disabled="getDisabled('width')"
      label="Ширина"
      class="small-width"
      placeholder="0.2"
      measure="м"
      big
      :max-value="maxDetailWidth"
      :min-value="0.01"
      :clearable="clearable"
      with-max-notice
      @change="changeDetailParam('width', $event)"
    />
    <vz-measure-input
      :modelValue="detailHeight"
      :disabled="getDisabled('height')"
      class="small-width"
      placeholder="0.2"
      big
      measure="м"
      :max-value="maxDetailHeight"
      :min-value="0.01"
      :clearable="clearable"
      with-max-notice
      @change="changeDetailParam('height', $event)"
    >
      <template #label>
        <span class="vz-input-label">Высота</span>
        <vz-warning-button
          v-if="interactive"
          v-show="shouldTurnOver"
          ref="turnRoundWarning"
          :toast-data="[turnOverToast]"
          :hidden="orderStore.hideOrderWrappingTurnOver"
          @update:hidden="orderStore.hideOrderWrappingTurnOver = $event"
        />
      </template>
    </vz-measure-input>
    <vz-measure-input
      :modelValue="detailWeight"
      :disabled="getDisabled('weight')"
      class="small-width"
      placeholder="0.9"
      big
      measure="кг"
      :max-value="maxDetailWeight"
      :min-value="0.1"
      :clearable="clearable"
      with-max-notice
      @change="changeDetailParam('weight', $event)"
    >
      <template #label>
        <span class="vz-input-label">Вес</span>
        <vz-warning-button
          v-if="interactive"
          v-show="dimensionsMod.weight()"
          ref="overloadWarnings"
          :toast-data="[overloadToast]"
          :hidden="orderStore.hideOrderWrappingOverload"
          @update:hidden="orderStore.hideOrderWrappingOverload = $event"
        />
      </template>
    </vz-measure-input>
    <vz-measure-input
      v-if="hasCount"
      :modelValue="count"
      :disabled="disabled"
      label="Кол-во мест такого типа"
      class="small-width vz-cargo-card-place"
      style="max-width: 178px"
      placeholder="1"
      big
      measure="шт"
      :max-value="999"
      :min-value="1"
      :allow-decimal="false"
      :clearable="clearable"
      with-max-notice
      @change="changeDetailParam(countName, $event)"
    />
  </div>
</template>

<script setup lang="ts">
import {PropType} from 'vue';
import {
  modChecker,
  OrderDimensionDetail,
  OrderWrappingItem,
  OrderWrappingModParameters,
  ToastConfigureObject
} from '~/types/types';
import {useWrappingStore} from '~/stores/wrapping';
import {getModCheckFunction} from '~/common/functions';
import VzWarningButton from '~/components/ui-kit/vz-warning-button.vue';
import { useOrderStore } from '~/stores/order';
import { storeToRefs } from 'pinia';
import VzMeasureInput from '~/components/ui-kit/vz-measure-input.vue';

const emit = defineEmits([
  'change-detail-param',
  'add-pallet',
  'set-mode'
])

const props = defineProps({
  detail: {
    type: Object as PropType<OrderDimensionDetail>,
    default: { height: null, length: null, weight: null, width: null }
  },
  wrapping: {
    type: Array as PropType<OrderWrappingItem[]>,
    default: () => ([])
  },
  count: {
    type: Number,
    default: 1
  },
  disabledFields: {
    type: Array as PropType<Array<keyof OrderDimensionDetail>>,
    default: () => ([])
  },
  disabled: {
    type: Boolean,
    default: false
  },
  interactive: {
    type: Boolean,
    default: true
  },
  hasCount: {
    type: Boolean,
    default: false
  },
  clearable: {
    type: Boolean,
    default: false
  },
  countName: {
    type: String,
    default: 'count'
  },
})

const wrappingStore = useWrappingStore()
const orderStore = useOrderStore()

const turnRoundWarning = ref({} as typeof VzWarningButton)
const overloadWarnings = ref({} as typeof VzWarningButton)

const detailHeight = computed(() => {
  return props.detail.height
})

const detailWidth = computed(() => {
  return props.detail.width
})

const detailLength = computed(() => {
  return props.detail.length
})

const detailWeight = computed(() => {
  return props.detail.weight
})

const maxDetailHeight = computed(() => {
  return orderStore.orderConditionValues?.cargo?.dimension?.detail?.height?.['<='] || 2.4
})

const maxDetailWidth = computed(() => {
  return orderStore.orderConditionValues?.cargo?.dimension?.detail?.width?.['<='] || 2.4
})

const maxDetailLength = computed(() => {
  return orderStore.orderConditionValues?.cargo?.dimension?.detail?.length?.['<='] || 12.9
})

const maxDetailWeight = computed(() => {
  return orderStore.orderConditionValues?.cargo?.dimension?.detail?.weight?.['<='] || 19999
})

const dimensionsMod = computed((): modChecker => {
  const mod: modChecker = {
    weight: () => false,
    weightProperty: '',
    weightMessage: ''
  }

  const weight = wrappingStore.mods.find(item => item.mod.condition.cargo?.dimension?.detail.weight)
  if (weight) {
    const weightValue = Object.values(weight.mod.condition.cargo?.dimension?.detail.weight as {'>=' : 1})[0]
    mod.weight = (): boolean => {
      const fc = getModCheckFunction(Object.keys(weight.mod.condition.cargo?.dimension?.detail.weight as {'>=' : 1})[0] as OrderWrappingModParameters['conditions'])
      return fc(parseInt(props.detail.weight as never as string), weightValue)
    }
    mod.weightProperty = weight.key
    mod.value = weightValue
    mod.weightMessage = weight.mod.message
  }

  return mod
})

const shouldTurnOver = computed(() => {
  return props.count === 1 && detailHeight.value > 1.7 && detailHeight.value > detailLength.value
})

const overloadToast = computed((): ToastConfigureObject => {
  const dimensionsModWrapping = props.wrapping.find(item => item.code === dimensionsMod.value.weightProperty)
  return {
    variant: 'primary',
    title: 'Постановка на паллет',
    text: overloadText(),
    autocloseDelay: 20000,
    cancelButtonText: dimensionsModWrapping ? undefined : 'Не нужно',
    buttonText: dimensionsModWrapping ? undefined : 'Воспользоваться',
    buttonCB: dimensionsModWrapping ? undefined : addPallet,
    //
    footerText: dimensionsModWrapping ? 'Упаковка добавлена' : undefined,
    footerVariant: dimensionsModWrapping ? 'success' : undefined,
    footerIconName: dimensionsModWrapping ? 'check-all' : undefined
  }
})

const turnOverToast = computed((): ToastConfigureObject => {
  return {
    variant: 'primary',
    title: 'Положить груз',
    text: 'Груз нестандартной высоты может увеличить стоимость заказа.' +
      ' Допустимо ли положить груз, если это не противоречит манипуляционным знакам?',
    autocloseDelay: 20000,
    cancelButtonText: 'Не нужно',
    buttonText: 'Положить',
    buttonCB: (): void => {
      const length = +detailLength.value
      const height = +detailHeight.value
      changeDetailParam('length', height)
      changeDetailParam('height', length)
    }
  }
})

function changeDetailParam(param: keyof OrderDimensionDetail | 'count' | string, value: string|number) {
  if (props.interactive && shouldTurnOver.value && (param === 'length' || param === 'height')) {
    // turnRoundWarning.value?.showToast?.()
  }
  emit('change-detail-param', { param, value })
  if (props.interactive && shouldTurnOver.value && (param === 'length' || param === 'height')) {
    turnRoundWarning.value?.showToast?.()
  }
}

function addPallet() {
  emit('add-pallet')
}

function setMode(value: string) {
  emit('set-mode', value)
}

function getDisabled(param: keyof OrderDimensionDetail): boolean {
  return props.disabled || props.disabledFields.includes(param)
}

function validateWeight() {
  if (props.interactive) {
    const dimensionsModWrapping = props.wrapping.find(item => item.code === dimensionsMod.value.weightProperty)
    if (dimensionsMod.value.weight() && !dimensionsModWrapping) {
      overloadWarnings.value?.showToast()
    }
  }
}

function overloadText(): any {
  return [
    h('span', 'Если Ваш груз располагается на паллете, рекомендуем воспользоваться '),
    h('a',
      {
        href: '#pallets',
        onClick: (e: Event) => {
          e.preventDefault()
          setMode('pallets')
          overloadWarnings.value?.hide()
        }
      },
      '«Вводом груза по паллетам»'
    ),
    h('span', `. Обратите внимание! Для грузов с весом одного места больше
       ${dimensionsMod.value.value} кг услуга упаковки «Постановка на паллет» обязательна.`)
  ]
}

watch(() => detailWeight.value,
  (value: number, oldValue: number) => {
    if (value !== oldValue) {
      validateWeight()
    }
  }
)

watch(() => [detailHeight.value, detailLength.value],
  (value, oldValue) => {
    if (props.interactive && shouldTurnOver.value && value.join(', ') !== oldValue.join(', ')) {
      turnRoundWarning.value?.showToast?.()
    }
  }
)
</script>
